import React from 'react';
import { Box, LinearProgress, Typography, useTheme } from '@mui/material';
import { Logo } from './Logo';
import { font } from '../../utils/fontHelper';

interface ActionLoaderProps {
  size?: number;
  title?: string;
  message?: string;
  showProgress?: boolean;
}

const ActionLoader: React.FC<ActionLoaderProps> = ({
  size = 72,
  title,
  message,
  showProgress = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const hasText = Boolean(title || message);

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          width: hasText ? 260 : size + 180,
          borderRadius: 3,
          bgcolor: isDark ? 'rgba(28, 28, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
          boxShadow: isDark
            ? '0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.1)'
            : '0 8px 32px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: hasText ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'center',
          px: hasText ? 3 : 0,
          pt: hasText ? 3 : 0,
          pb: hasText ? 2.5 : 0,
          gap: hasText ? 1.5 : 0,
          height: hasText ? 'auto' : size + 32,
        }}
      >
        <Logo
          size={size === 72 ? 'large' : 'medium'}
          variant="default"
          state="fast-blink"
        />

        {hasText && (
          <Box sx={{ textAlign: 'center' }}>
            {title && (
              <Typography
                sx={{
                  fontFamily: font(),
                  fontWeight: 600,
                  fontSize: 14,
                  color: isDark ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.87)',
                  lineHeight: 1.4,
                  mb: message ? 0.5 : 0,
                }}
              >
                {title}
              </Typography>
            )}
            {message && (
              <Typography
                sx={{
                  fontFamily: font(),
                  fontWeight: 400,
                  fontSize: 12,
                  color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
                  lineHeight: 1.4,
                }}
              >
                {message}
              </Typography>
            )}
          </Box>
        )}

        {showProgress && (
          <LinearProgress
            sx={{
              width: '100%',
              borderRadius: 4,
              height: 2,
              bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                background: 'linear-gradient(90deg, #6C64FD, #EBA9FC)',
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default ActionLoader; 